import React, { createContext, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UseInfiniteQueryResult, UseQueryResult, useInfiniteQuery, useQuery } from 'react-query';
import { differenceInHours, isAfter, sub } from 'date-fns';
import { isUndefined, uniqWith } from 'lodash';
import { AvailableTimes } from 'domains/calendars/types';
import { getRescheduleAvailableTimes } from 'domains/calendars/queries';
import { MAX_RESCHEDULE_DAYS_RANGE } from 'domains/appointments/reschedule/calendar';
import { getReschedule } from './queries';
import { Reschedule } from './types';

type RescheduleStateProviderContextValue = {
  timeSlot?: Date;
  clientId?: string;
  rescheduleId?: string;
  rescheduleQuery: UseQueryResult<Reschedule, unknown>;
  availableTimesQuery: UseInfiniteQueryResult<AvailableTimes, unknown>;
  availableTimes: AvailableTimes;
  rescheduleInformation?: Reschedule;
  setTimeSlot: React.Dispatch<React.SetStateAction<Date | undefined>>;
};

const RescheduleStateContext = createContext<RescheduleStateProviderContextValue | undefined>(undefined);

// dev-note: to give therapist time for preparation, any pending reschedule can be
// rescheduled at least 2 hours before the appointment
export const RESCHEDULE_MAX_TIME_GAP = 2;
export const isTimeSlotAvailable = (timeSlot: Date): boolean => {
  return differenceInHours(timeSlot, new Date()) >= RESCHEDULE_MAX_TIME_GAP;
};

export const RescheduleStateProvider: React.FC = ({ children }) => {
  const [timeSlot, setTimeSlot] = useState<Date | undefined>();

  const { clientId, rescheduleId } = useParams<{ clientId: string; rescheduleId: string }>();

  if (isUndefined(clientId) || isUndefined(rescheduleId)) {
    throw new Error(`Bad reschedule URL missing clientId (${clientId}) OR rescheduleId (${rescheduleId})`);
  }

  const rescheduleQuery = useQuery(['reschedule', clientId, rescheduleId], () => getReschedule(clientId, rescheduleId));

  const availableTimesQuery = useInfiniteQuery(
    ['reschedule-available-times', clientId, rescheduleId],
    ({ pageParam }) => {
      const { originalAcuityAppointment: appointment } = rescheduleQuery.data || {};
      if (!appointment) {
        throw new Error(`Expected appointment to be defined`);
      }
      const today = new Date();
      const minRescheduleDate = sub(new Date(appointment.datetime), { days: MAX_RESCHEDULE_DAYS_RANGE });
      const queryDatesFrom = isAfter(minRescheduleDate, today) ? minRescheduleDate : today;

      return getRescheduleAvailableTimes(
        clientId,
        rescheduleQuery.data?.originalAcuityAppointment.id ?? 0,

        new Date(pageParam ?? queryDatesFrom),
      );
    },
    {
      retry: false,
      enabled: rescheduleQuery.isSuccess && rescheduleQuery.data?.status === 'active',
    },
  );

  const availableTimesSlots = useMemo(() => {
    return availableTimesQuery.data?.pages?.flat().map(currentSlot => {
      return {
        ...currentSlot,
        availableTimes: currentSlot.availableTimes?.filter(isTimeSlotAvailable),
      };
    });
  }, [availableTimesQuery.data]);

  const ctxValue: RescheduleStateProviderContextValue = {
    timeSlot,
    clientId,
    rescheduleId,
    rescheduleQuery,
    availableTimesQuery,
    availableTimes: uniqWith(availableTimesSlots, (a, b) => a.day.toISOString() === b.day.toISOString()),
    rescheduleInformation: rescheduleQuery.data,
    setTimeSlot,
  };

  return <RescheduleStateContext.Provider value={ctxValue}>{children}</RescheduleStateContext.Provider>;
};

export const useRescheduleState = () => {
  const ctx = useContext(RescheduleStateContext);

  if (!ctx) {
    throw new Error(`You must wrap your component to a reschedule state context!`);
  }

  return ctx;
};
